import { useLiveQuery } from '@electric-sql/pglite-react';
import { formatDateTimeIST } from '@quickreport/domain';

interface AuditRow {
  id: string;
  action: string;
  actor: string;
  at: string;
  before: Record<string, string> | { status?: string } | null;
  after: Record<string, string> | { status?: string } | null;
  reason: string | null;
}
interface VersionRow {
  version: number;
  signed_at: string;
  amend_reason: string | null;
}

const LABEL: Record<string, string> = {
  created: 'Report created',
  result_entered: 'Result entered',
  result_changed: 'Result changed',
  signed: 'Signed',
  amended: 'Amended',
  revoked: 'Revoked',
  status_changed: 'Status changed',
  renamed: 'Renamed',
  due_delayed: 'Due date changed',
  due_completed: 'Marked complete',
};

/** Everything that happened to this report, oldest first: versions signed and each change. */
export function HistoryPanel({ reportId }: { reportId: string }) {
  const audit = useLiveQuery<AuditRow>(
    `select id, action, actor, at, before, after, reason from audit_events where entity_id = $1 and entity = 'report' order by at, id`,
    [reportId],
  );
  const versions = useLiveQuery<VersionRow>(
    'select version, signed_at, amend_reason from report_versions where report_id = $1 order by version',
    [reportId],
  );
  const members = useLiveQuery<{ user_id: string; display_name: string }>(
    'select user_id, display_name from members',
    [],
  );
  const nameOf = (uid: string) =>
    members?.rows.find((m) => m.user_id === uid)?.display_name || 'Someone';

  return (
    <div className="grid gap-5 p-4">
      <section>
        <h3 className="mb-2 text-[13px] font-semibold">Signed versions</h3>
        {(versions?.rows ?? []).length === 0 ? (
          <p className="text-[13px] text-slate">Not signed yet.</p>
        ) : (
          <ul className="grid gap-1.5">
            {versions?.rows.map((v) => (
              <li key={v.version} className="rounded-md bg-canvas px-3 py-2 text-[13px]">
                <b>Version {v.version}</b> · {formatDateTimeIST(v.signed_at)}
                {v.amend_reason ? (
                  <div className="text-slate">Amended: {v.amend_reason}</div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section>
        <h3 className="mb-2 text-[13px] font-semibold">Activity</h3>
        <ol className="grid gap-2 border-l border-line pl-3">
          {(audit?.rows ?? []).map((a) => {
            const change = a.action.startsWith('result_') ? Object.entries(a.after ?? {})[0] : null;
            const was = a.action === 'result_changed' ? Object.values(a.before ?? {})[0] : null;
            return (
              <li key={a.id} className="text-[13px]">
                <div>
                  <b>{LABEL[a.action] ?? a.action}</b> · {nameOf(a.actor)}
                </div>
                <div className="text-[12px] text-slate">{formatDateTimeIST(a.at)}</div>
                {change ? (
                  <div className="text-[12px] text-slate">
                    {change[0]}: {was ? `${was} → ` : ''}
                    <b className="text-ink">{String(change[1])}</b>
                  </div>
                ) : null}
                {a.reason ? <div className="text-[12px] text-slate">Reason: {a.reason}</div> : null}
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
}
